import { LangText } from '@/components';
import { FOOTER_EXTERNAL } from '@/constants/footerExternal';
import { APP_LOCALES, LOCALE_LABELS, isAppLocale, type AppLocale } from '@/constants/appLocale';
import { preFixImgUrl } from '@/utils';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import customIcon from '@/assets/svg/custom.svg';
import scanIcon from '@/assets/svg/scan.svg';
import jsCookie from 'js-cookie';

const FOOTER_WIDTH_CLASS = 'mx-auto w-[1440px] px-[20px]';

const FOOTER_LINK_COLUMNS = [
  {
    titleKey: 'layout.footer.sections.billionPass.title',
    links: [
      { lngKey: 'layout.footer.sections.billionPass.about', to: '/about-us' },
      { lngKey: 'layout.footer.sections.billionPass.terms', to: '/terms' },
      { lngKey: 'layout.footer.sections.billionPass.privacy', to: '/privacy' },
    ],
  },
  {
    titleKey: 'layout.footer.sections.partnership.title',
    links: [
      { lngKey: 'layout.footer.sections.partnership.merchantRegister', disabled: true },
      { lngKey: 'layout.footer.sections.partnership.merchantLogin', disabled: true },
    ],
  },
  {
    titleKey: 'layout.footer.sections.about.title',
    links: [
      { lngKey: 'layout.footer.sections.about.aboutUs', href: FOOTER_EXTERNAL.company, external: true },
      { lngKey: 'layout.footer.sections.about.contact', href: FOOTER_EXTERNAL.company, external: true },
    ],
  },
] as const;

function FooterLink({
  lngKey,
  to,
  href,
  external,
  disabled,
}: {
  lngKey: string;
  to?: string;
  href?: string;
  external?: boolean;
  disabled?: boolean;
}) {
  if (disabled) {
    return (
      <span className="cursor-default text-[16px] leading-[24px] text-[#D0D0D0]">
        <LangText lngKey={lngKey} />
      </span>
    );
  }

  const className = 'text-[16px] leading-[24px] text-[#979797] hover:text-primary transition-colors';

  if (to) {
    return (
      <Link to={to} className={className}>
        <LangText lngKey={lngKey} />
      </Link>
    );
  }

  return (
    <a
      href={href}
      target={external ? '_blank' : undefined}
      rel={external ? 'noopener noreferrer' : undefined}
      className={className}
    >
      <LangText lngKey={lngKey} />
    </a>
  );
}

export function Footer() {
  const year = new Date().getFullYear();
  const { i18n } = useTranslation();
  const { language } = i18n;

  const handleLangChange = (locale: AppLocale) => {
    if (locale === language) return;
    jsCookie.set('i18next', locale);
    localStorage.setItem('i18next', locale);
    window.location.reload();
  };

  return (
    <footer className="w-full bg-white">
      <div className={`pb-[36px] pt-[68px] ${FOOTER_WIDTH_CLASS}`}>
        <div className="flex justify-between items-start">
          <div className="flex flex-col gap-[36px]">
            <div>
              <img
                src={preFixImgUrl('41683676bd21e8531742d57a8f7a2570.png')}
                alt="BillionPass"
                className="h-[53px] w-auto"
                draggable={false}
              />
              <p className="mt-[30px] text-[16px] leading-[28px] text-[#979797]">
                <LangText lngKey="layout.footer.brand.description" />
              </p>
            </div>

            <div className="flex items-center">
              <div className="shrink-0 border-[3px] border-primary">
                <img
                  src={preFixImgUrl('d4d23c301633465c2f5febbb8df18938.png')}
                  alt="WeChat QR Code"
                  className="h-[68px] w-[68px] object-contain"
                  draggable={false}
                />
              </div>

              <div className="flex flex-col items-center ml-[20px]">
                <img src={scanIcon} alt="" className="h-[44px] w-[44px] object-contain" />
                <div className="text-center text-[14px] leading-[20px] text-[#979797]">
                  <p><LangText lngKey="layout.footer.wechat.scan" /></p>
                  <p><LangText lngKey="layout.footer.wechat.addService" /></p>
                </div>
              </div>

              <div className="flex flex-col items-center ml-[40px]">
                <img src={customIcon} alt="" className="h-[44px] w-[44px] object-contain" />
                <div className="text-center text-[14px] leading-[20px] text-[#979797]">
                  <p><LangText lngKey="layout.footer.customerService.email" /></p>
                  <a
                    href="mailto:bilionpass@jdxjp.net"
                    className="text-[#999999] hover:text-primary transition-colors"
                  >
                    bilionpass@jdxjp.net
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-[80px]">
            {FOOTER_LINK_COLUMNS.map(({ titleKey, links }) => (
              <div key={titleKey} className="flex flex-col gap-[26px]">
                <span className="text-[24px] font-bold leading-[24px] text-[#000]">
                  <LangText lngKey={titleKey} />
                </span>
                <div className="flex flex-col gap-[16px]">
                  {links.map((link) => (
                    <FooterLink key={link.lngKey} {...link} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="border-t border-[#E8E8E8]">
        <div className={`flex justify-between items-center pt-[15px] pb-[68px] ${FOOTER_WIDTH_CLASS}`}>
          <span className="text-[14px] leading-[22px] text-[#B0B0B0]">
            © {year} Billion Pass. All rights reserved.
          </span>
          <div className="flex gap-6 items-center">
            {APP_LOCALES.map((locale) => {
              const isActive = isAppLocale(language) && language === locale;
              return (
                <button
                  key={locale}
                  type="button"
                  onClick={() => handleLangChange(locale)}
                  className={`text-[14px] leading-[22px] transition-colors ${
                    isActive ? 'text-[#333333]' : 'text-[#B0B0B0] hover:text-primary'
                  }`}
                >
                  {LOCALE_LABELS[locale]}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </footer>
  );
}
